import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { mediaUrl } from "@/lib/api";

const PlayerContext = createContext(null);

export function PlayerProvider({ children }) {
  const audioRef = useRef(null);
  const [current, setCurrent] = useState(null); // track object
  const [queue, setQueue] = useState([]);
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.85);

  useEffect(() => {
    if (!audioRef.current) {
      audioRef.current = new Audio();
      audioRef.current.volume = volume;
    }
    const a = audioRef.current;
    const onTime = () => setProgress(a.currentTime);
    const onLoaded = () => setDuration(a.duration || 0);
    const onEnded = () => playNext();
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("loadedmetadata", onLoaded);
    a.addEventListener("ended", onEnded);
    return () => {
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("loadedmetadata", onLoaded);
      a.removeEventListener("ended", onEnded);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = volume;
  }, [volume]);

  const playTrack = (track, list = null) => {
    const a = audioRef.current;
    if (!a) return;
    const newQueue = list && list.length ? list : [track];
    const idx = newQueue.findIndex((t) => t.id === track.id);
    setQueue(newQueue);
    setIndex(idx >= 0 ? idx : 0);
    setCurrent(track);
    a.src = mediaUrl(track.audio_url);
    a.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
  };

  const togglePlay = () => {
    const a = audioRef.current;
    if (!a || !current) return;
    if (a.paused) {
      a.play().then(() => setPlaying(true)).catch(() => {});
    } else {
      a.pause();
      setPlaying(false);
    }
  };

  const seek = (sec) => {
    if (audioRef.current) audioRef.current.currentTime = sec;
  };

  const playNext = () => {
    if (!queue.length) return;
    const next = (index + 1) % queue.length;
    setIndex(next);
    const t = queue[next];
    if (t) playTrack(t, queue);
  };

  const playPrev = () => {
    if (!queue.length) return;
    const prev = (index - 1 + queue.length) % queue.length;
    setIndex(prev);
    const t = queue[prev];
    if (t) playTrack(t, queue);
  };

  return (
    <PlayerContext.Provider
      value={{ current, playing, progress, duration, volume, setVolume, playTrack, togglePlay, seek, playNext, playPrev }}
    >
      {children}
    </PlayerContext.Provider>
  );
}

export const usePlayer = () => useContext(PlayerContext);
